<template>
  <div class="c-bitts-banner" :class="props.variant">
    <div class="w-full flex items-center justify-center gap-8">
      <FontAwesomeIcon
        v-if="props.icon"
        :icon="props.icon"
        :style="{ height: '16px', width: '16px' }"
      />
      <p name="message" class="c-bitts-banner__message" v-html="props.message">
      </p>
      <BittsButton
        v-if="props.button?.text"
        :text="props.button?.text"
        size="x-small"
        variant="outline"
        :left-icon="props.button?.icon"
        :type="buttonType"
        class="c-bitts-banner__action-button ml-8"
        data-testid="c-bitts-banner__action-button"
        @click="handleAlertClick"
      />
    </div>
    <BittsButton
      v-if="props.dismissable"
      aria-label="Dismiss"
      data-testid="c-bitts-banner__dismiss"
      :class="props.variant"
      class="ml-auto"
      :center-icon="['fak', 'x']"
      :type="buttonType"
      variant="ghost"
      size="x-small"
      @click="emit('dismissed', props.type)"
    />
  </div>
</template>

<script
  setup
  lang="ts"
  generic="AlertType extends string = string, AlertData = unknown"
>
import { computed } from 'vue';

import BittsButton from '../BittsButton/BittsButton.vue';
import { BittsIcon } from '../types';

const props = withDefaults(
  defineProps<{
    message: string;
    icon?: BittsIcon;
    button?: { text?: string; icon?: BittsIcon } | null;
    dismissable?: boolean;
    type?: AlertType;
    variant?: string;
    alert?: AlertData | null;
  }>(),
  {
    icon: null,
    button: null,
    dismissable: false,
    type: undefined,
    variant: 'info',
    alert: null,
  },
);

const emit = defineEmits<{
  dismissed: [type?: AlertType];
  clicked: [payload: { type?: AlertType; alert: AlertData | null }];
}>();

const variants = {
  INFO: 'info',
  NEUTRAL: 'neutral',
  DANGER: 'danger',
  WARNING: 'warning',
  PRIMARY: 'primary',
  UPSELL: 'upsell',
  EVENT: 'event',
};

const buttonType = computed(() => {
  if (
    [
      variants.INFO,
      variants.NEUTRAL,
      variants.DANGER,
      variants.UPSELL,
      variants.EVENT,
    ].includes(props.variant)
  )
    return 'white';
  return 'primary';
});

function handleAlertClick() {
  emit('clicked', { type: props.type, alert: props.alert });
}
</script>
<style lang="pcss" scoped>
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.c-bitts-banner {
  @apply w-full flex items-center justify-center px-16 py-8 h-40;

  &.danger {
    @apply text-white bg-linear-to-r from-danger-background-medium to-danger-text;
  }

  &.warning {
    @apply text-warning-text-strong;
    background: var(
      --background-warning-gradient,
      linear-gradient(
        90deg,
        theme(colors.warning.background) 0%,
        theme(colors.warning.border) 100%
      )
    );

    .c-bitts-banner__action-button {
      @apply bg-transparent;
    }
  }

  &.upsell {
    @apply text-white;
    background: var(
      --background-warning-gradient,
      linear-gradient(
        90deg,
        theme(colors.upsell.background / 0.9) 0%,
        theme(colors.upsell.text) 100%
      )
    );

    .c-bitts-banner__action-button {
      @apply bg-transparent;
    }
  }

  &.info {
    @apply text-white  bg-linear-to-r from-info-accent to-info-text;
  }

  &.neutral {
    @apply text-white bg-linear-to-r from-neutral-400 to-neutral-600;
  }

  &.primary {
    @apply text-white bg-linear-to-t from-denim-400 to-brand-navy;
  }

  &.admin {
    @apply bg-red-300 text-neutral-text-strong;
  }

  &.event {
    @apply text-white  bg-linear-to-r from-event-accent to-event-text;
    .c-bitts-banner__message :deep(a) {
      @apply font-bold underline;
      text-decoration-thickness: 1px;
      text-underline-offset: 3px;
      transition: opacity 0.2s ease;
    }
    .c-bitts-banner__message :deep(a):hover {
      @apply opacity-80;
    }
    .c-bitts-banner__message :deep(a):focus {
      @apply outline-hidden ring-2 ring-white ring-offset-2 ring-offset-transparent;
    }
  }
}

.c-bitts-banner__message {
  @apply text-base text-center;
}
</style>
